import React, { FC, useContext, useEffect, useState } from 'react';
import { Button, Box, LinearProgress, Paper, Chip, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useLazyQuery, useMutation } from '@apollo/client';
import { scannerCaseTemplate } from 'apollo/templates/scanner-case';
import { ScannerCase, ScannerFileProcessingStatus } from 'models/scanner-case';
import InfoText from 'components/shared/info-text.component';
import RestorationsTable from '../restorations-table/restorations-table.component';
import ScannerCaseDenthubFileTable from '../scanner-case-denthub-file-table/scanner-case-denthub-file-table.component';
import { socketIo } from 'context/socket';
import ScannerCaseInfo from '../scanner-case-info/scanner-case-info.component';
import ScannerCasePreview from './scanner-case-preview/scanner-case-preview.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { startFileImportBackgroundProcessTemplate } from 'apollo/templates/scanner-case';

type ScannerCaseDialogProps = {
    open: boolean;
    caseId: string;
    onDialogClose: () => void;
    onDoNotImport?: (caseId: string) => void;
    onCreateOrderFromScanCase?: (caseId: string) => void;
    displayCreateOrderButton?: boolean;
    displayDoNotImportButton?: boolean;
};
const ScannerCaseDialog: FC<ScannerCaseDialogProps> = ({
    open,
    caseId,
    onDialogClose,
    onDoNotImport,
    onCreateOrderFromScanCase,
    displayCreateOrderButton,
    displayDoNotImportButton,
}) => {
    const { t: translate } = useTranslation(['scan-cases', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const socket = useContext(socketIo.getSocketContext());
    const [scannerCase, setScannerCase] = useState<ScannerCase>();
    const currentOrg = currentOrgVar();

    const [getScannerCase, { loading, refetch }] = useLazyQuery(scannerCaseTemplate, {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        variables: {
            caseId,
        },
        onCompleted({ scannerCase }) {
            setScannerCase(scannerCase);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (open && caseId) {
            getScannerCase();
        }
    }, [open, caseId]);

    useEffect(() => {
        const handleFilesProcessComplete = (event: { data: { scannerCaseId: string } }) => {
            if (event.data.scannerCaseId === caseId) {
                console.log('REFETCHING SCANNER CASE DATA');
                refetch();
            }
        };

        socket.on('SCANNER_CASE_FILES_PROCESS_COMPLETE', handleFilesProcessComplete);
        return () => {
            // Remove only this listener — removeAllListeners would also detach the
            // handlers registered by the parent table and by ScannerCaseInfo.
            socket.off('SCANNER_CASE_FILES_PROCESS_COMPLETE', handleFilesProcessComplete);
        };
    }, [socket, caseId]);

    const patientTitle = () => {
        let title = translate('scan-cases:NO_PATIENT_NAME');
        if (scannerCase?.patient?.firstName) {
            title = `${scannerCase.patient?.firstName} ${scannerCase.patient?.lastName || ''}`;
        }
        if (scannerCase?.patient?.patientRefNo) {
            title += ` - ${scannerCase?.patient?.patientRefNo}`;
        }
        return title;
    };

    const [startFileImportBackgroundProcess] = useMutation(startFileImportBackgroundProcessTemplate, {
        onCompleted: ({ startFileImportBackgroundProcess }) => {
            if (startFileImportBackgroundProcess) {
                refetch();
            }
        },

        onError: (error) => {
            showBoundary(error);
        },
    });

    const handleTryAgain = () => {
        startFileImportBackgroundProcess({ variables: { caseId: caseId } });
    };

    const isAwaitingFiles =
        scannerCase?.fileProcessingStatus === ScannerFileProcessingStatus.NOT_STARTED ||
        scannerCase?.fileProcessingStatus === ScannerFileProcessingStatus.PROCESSING;

    return (
        <DHDialog
            fullWidth
            maxWidth="lg"
            open={open}
            onClose={onDialogClose}
            title={
                <>
                    {!loading ? (
                        <Box display="flex" alignItems="center">
                            <Box mr={1}>{patientTitle()}</Box>
                            <Chip color="info" size="small" label={translate(`scan-cases:${scannerCase?.source}`)} />
                        </Box>
                    ) : undefined}
                </>
            }
            actions={
                displayCreateOrderButton || displayDoNotImportButton ? (
                    <>
                        {displayDoNotImportButton && onDoNotImport ? (
                            <Button onClick={() => onDoNotImport(caseId)}>
                                {translate('scan-cases:DO_NOT_IMPORT')}
                            </Button>
                        ) : undefined}
                        {displayCreateOrderButton && onCreateOrderFromScanCase ? (
                            <Button disabled={isAwaitingFiles} onClick={() => onCreateOrderFromScanCase(caseId)}>
                                {translate('scan-cases:CREATE_DENTHUB_ORDER')}
                            </Button>
                        ) : undefined}
                    </>
                ) : undefined
            }
        >
            {loading || !scannerCase ? (
                <LinearProgress />
            ) : (
                <Box>
                    <Box mb={2}>
                        <ScannerCaseInfo scannerCase={scannerCase} />
                    </Box>

                    <Box mb={2}>
                        <ScannerCasePreview scannerCase={scannerCase} cols={5} />
                    </Box>

                    <Box mb={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            {scannerCase.comments ? (
                                <Box>{scannerCase.comments}</Box>
                            ) : (
                                <InfoText text={translate('orders:NO_COMMENTS_AVAILABLE')} />
                            )}
                        </Paper>
                    </Box>
                    <Box mb={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <RestorationsTable restorations={scannerCase.restorations} />
                        </Paper>
                    </Box>
                    <Box mb={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            {/* Remove ScannerCaseSourceFileTable that allow download directly from the source scanner */}
                            {scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.COMPLETED ? (
                                <ScannerCaseDenthubFileTable scannerCase={scannerCase} />
                            ) : undefined}
                            {scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.NOT_STARTED ||
                            scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.PROCESSING ? (
                                <Alert severity="info">{translate('scan-cases:SCANNER_FILES_STILL_PROCESSING')}</Alert>
                            ) : undefined}
                            {scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.ERROR &&
                            currentOrg?._id.toString() === scannerCase?.organization?._id.toString() ? (
                                <Alert
                                    severity="error"
                                    action={
                                        <Button size="small" onClick={handleTryAgain}>
                                            {translate('common:TRY_AGAIN')}
                                        </Button>
                                    }
                                >
                                    {translate('scan-cases:SCANNER_FILES_PROCESSING_ERROR')}
                                </Alert>
                            ) : undefined}
                        </Paper>
                    </Box>
                </Box>
            )}
        </DHDialog>
    );
};

export default ScannerCaseDialog;
